import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors } from "@tabbio/tokens";
import type { ApplicationStudioArtifactSummary } from "@tabbio/types";
import { Copy } from "lucide-react";
import {
  APPLICATION_STUDIO_TOOLS,
  STUDIO_SENIORITIES,
  artifactKindLabel,
  type ApplicationStudioToolId,
  type StudioCountryCode,
  type StudioSeniority,
} from "../../lib/application-studio-tool-config";
import {
  createApplicationStudioProject,
  fetchApplicationStudioProject,
  generateApplicationStudioArtifact,
  isApplicationStudioEntitlementDenied,
} from "../../lib/application-studio-api";
import { useApplicationStudioArtifacts } from "../../hooks/useApplicationStudioApi";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../hooks/useToast";
import {
  applicationStudioArtifactCopyText,
  cleanStudioMarkdownText,
  studioMarkdownSegments,
} from "../../lib/application-studio-text";
import { WebPageHeader } from "../common/WebPageHeader";
import { StudioCountryPicker } from "./StudioCountryPicker.web";
import { dismissToServices } from "../../lib/services-navigation";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useUpgradeModal } from "../../providers/UpgradeModalProvider";
import { createUpgradeContext } from "../../lib/monetization-context";
import { trackProductEvent } from "../../lib/product-analytics";
import { STUDIO_TOOL_ANALYTICS_EVENTS } from "../../lib/application-studio-tool-config";
import { usePublishPageContext } from "../../lib/page-context";

type Props = {
  toolId: ApplicationStudioToolId;
};

function contentOf(
  artifact: ApplicationStudioArtifactSummary | null | undefined,
) {
  return artifact?.content && typeof artifact.content === "object"
    ? artifact.content
    : {};
}

function stringList(value: unknown) {
  return Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === "string" && Boolean(item.trim()),
      )
    : [];
}

function objectList(value: unknown) {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function numberValue(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function scoreText(value: number | null) {
  return value === null ? "--" : `${Math.round(value)}`;
}

function formatSalaryValue(currency: string, value: number | null) {
  return value === null ? "--" : `${currency} ${value.toLocaleString()}`;
}

async function copyTextToClipboard(value: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard is unavailable");
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("Clipboard copy failed");
  }
}

function StudioRichText({
  value,
  style,
}: {
  value: string;
  style: React.CSSProperties;
}) {
  const segments = useMemo(() => studioMarkdownSegments(value), [value]);

  return (
    <div dir="auto" style={style}>
      {segments.map((segment, index) => (
        <span
          key={`${index}-${segment.text}`}
          style={{
            fontWeight: segment.strong ? 700 : undefined,
            fontFamily: segment.code
              ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
              : undefined,
            color:
              segment.strong || segment.code ? "var(--tabbio-text)" : undefined,
          }}
        >
          {segment.text}
        </span>
      ))}
    </div>
  );
}

function CopyArtifactButton({
  artifact,
}: {
  artifact: ApplicationStudioArtifactSummary;
}) {
  const { t } = useTranslation("seeker");
  const { theme } = useTheme();
  const toast = useToast();
  const [hovered, setHovered] = useState(false);

  const onCopy = async () => {
    try {
      await copyTextToClipboard(
        applicationStudioArtifactCopyText(
          artifact,
          artifactKindLabel(artifact.kind),
        ),
      );
      toast.success(t("studioTools.copied"));
    } catch {
      toast.error(t("studioTools.copyFailed"));
    }
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        minHeight: 30,
        borderRadius: 999,
        border: `1px solid ${theme.borderSubtle}`,
        backgroundColor: hovered
          ? theme.buttonGhostHover
          : theme.surfaceSecondary,
        color: theme.textSecondary,
        padding: "6px 10px",
        fontSize: 12,
        fontWeight: 650,
        fontFamily: "inherit",
        cursor: "pointer",
        transition:
          "background-color 150ms ease, color 150ms ease, transform 150ms ease",
      }}
    >
      <Copy size={14} strokeWidth={2.2} />
      {t("studioTools.copy")}
    </button>
  );
}

function ResultHeader({
  title,
  artifact,
  subtle,
}: {
  title: string;
  artifact: ApplicationStudioArtifactSummary;
  subtle?: boolean;
}) {
  const { theme } = useTheme();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div
        style={{
          ...(subtle ? resultKickerStyle(theme) : resultTitleStyle(theme)),
          minWidth: 0,
          flex: 1,
        }}
      >
        {cleanStudioMarkdownText(title)}
      </div>
      <CopyArtifactButton artifact={artifact} />
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  const { theme } = useTheme();
  const [hovered, setHovered] = useState(false);
  const interactive = Boolean(onClick);

  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "8px 12px",
        border: `1px solid ${active ? theme.buttonSoftBg : theme.borderSubtle}`,
        backgroundColor: active
          ? theme.buttonSoftBg
          : hovered && interactive
            ? theme.surfaceSecondary
            : theme.surface,
        color: active ? theme.buttonSoftText : theme.textSecondary,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: interactive ? "pointer" : "default",
        transition: "background-color 150ms ease, color 150ms ease",
      }}
    >
      {label}
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: "var(--tabbio-text-secondary)",
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  multiline,
  compact,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  multiline?: boolean;
  compact?: boolean;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Label>{label}</Label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          rows={compact ? 3 : 6}
          dir="auto"
          style={{
            width: "100%",
            resize: "vertical",
            borderRadius: 12,
            border: "1px solid var(--tabbio-border)",
            background: "var(--tabbio-bg)",
            color: "var(--tabbio-text)",
            padding: 14,
            fontSize: 14,
            lineHeight: "20px",
            fontFamily: "inherit",
            outline: "none",
          }}
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          dir="auto"
          style={{
            width: "100%",
            borderRadius: 999,
            border: "1px solid var(--tabbio-border)",
            background: "var(--tabbio-bg)",
            color: "var(--tabbio-text)",
            padding: "13px 17px",
            fontSize: 14,
            fontFamily: "inherit",
            outline: "none",
          }}
        />
      )}
    </label>
  );
}

function BulletList({ title, items }: { title: string; items: string[] }) {
  const { theme } = useTheme();
  if (items.length === 0) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>
        {title}
      </div>
      <div
        style={{
          borderRadius: 22,
          border: `1px solid ${theme.borderSubtle}`,
          backgroundColor: theme.surface,
          overflow: "hidden",
        }}
      >
        {items.slice(0, 8).map((item, index) => (
          <div
            key={item}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: "12px 14px",
              borderBottom:
                index < Math.min(items.length, 8) - 1
                  ? `1px solid ${theme.borderSubtle}`
                  : "none",
            }}
          >
            <div
              style={{
                width: 28,
                color: theme.textTertiary,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: "20px",
              }}
            >
              {index + 1}
            </div>
            <StudioRichText
              value={item}
              style={{
                flex: 1,
                color: theme.textSecondary,
                fontSize: 14,
                lineHeight: "21px",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function ScoreRow({ label, value }: { label: string; value: number | null }) {
  const { theme } = useTheme();
  const pct = value === null ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
      >
        <span
          style={{ color: theme.textSecondary, fontSize: 13, fontWeight: 600 }}
        >
          {label}
        </span>
        <span style={{ color: theme.text, fontSize: 13, fontWeight: 700 }}>
          {scoreText(value)}
        </span>
      </div>
      <div
        style={{
          height: 9,
          borderRadius: 999,
          overflow: "hidden",
          backgroundColor: theme.surfaceSecondary,
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor: colors.primary,
          }}
        />
      </div>
    </div>
  );
}

function ResultRenderer({
  artifact,
  compact,
}: {
  artifact: ApplicationStudioArtifactSummary;
  compact?: boolean;
}) {
  const { t } = useTranslation("seeker");
  const { theme } = useTheme();
  const content = contentOf(artifact);

  if (compact) {
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={artifactKindLabel(artifact.kind)}
          artifact={artifact}
          subtle
        />
        <div style={resultTitleStyle(theme)}>
          {cleanStudioMarkdownText(
            artifact.title ?? artifactKindLabel(artifact.kind),
          )}
        </div>
        <StudioRichText
          value={artifact.plaintext ?? t("studioTools.results.savedArtifact")}
          style={resultBodyStyle(theme)}
        />
      </article>
    );
  }

  if (artifact.kind === "cv_fit_report") {
    const overall = numberValue(content.overallScore);
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={artifactKindLabel(artifact.kind)}
          artifact={artifact}
          subtle
        />
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              backgroundColor: theme.buttonSoftBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: theme.buttonSoftText,
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            {scoreText(overall)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={resultTitleStyle(theme)}>
              {t("studioTools.results.cvScore")}
            </div>
            <StudioRichText
              value={stringValue(content.summary) ?? artifact.plaintext ?? ""}
              style={{ ...resultBodyStyle(theme), marginTop: 4 }}
            />
          </div>
        </div>
        <ScoreRow
          label={t("studioTools.results.gccFit")}
          value={numberValue(content.gccFitScore)}
        />
        <ScoreRow
          label={t("studioTools.results.ats")}
          value={numberValue(content.atsScore)}
        />
        <BulletList
          title={t("studioTools.results.missingSkills")}
          items={stringList(content.missingSkills)}
        />
        <BulletList
          title={t("studioTools.results.nextActions")}
          items={stringList(content.nextActions)}
        />
      </article>
    );
  }

  if (artifact.kind === "salary_report") {
    const currency = stringValue(content.currency) ?? "";
    const confidence = stringValue(content.confidence);
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={t("studioTools.results.salary")}
          artifact={artifact}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Chip
            label={`${t("studioTools.results.low")}: ${formatSalaryValue(currency, numberValue(content.low))}`}
          />
          <Chip
            label={`${t("studioTools.results.median")}: ${formatSalaryValue(currency, numberValue(content.median))}`}
          />
          <Chip
            label={`${t("studioTools.results.high")}: ${formatSalaryValue(currency, numberValue(content.high))}`}
          />
          {confidence ? (
            <Chip
              label={`${t("studioTools.results.confidence")}: ${confidence}`}
            />
          ) : null}
        </div>
        <StudioRichText
          value={
            stringValue(content.negotiationScript) ?? artifact.plaintext ?? ""
          }
          style={resultBodyStyle(theme)}
        />
        <BulletList
          title={t("studioTools.results.marketNotes")}
          items={stringList(content.marketNotes)}
        />
      </article>
    );
  }

  if (artifact.kind === "recruiter_preview") {
    const profileScore = numberValue(content.profileScore);
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={artifactKindLabel(artifact.kind)}
          artifact={artifact}
          subtle
        />
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 999,
              backgroundColor: theme.buttonSoftBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: theme.buttonSoftText,
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            {scoreText(profileScore)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={resultTitleStyle(theme)}>
              {cleanStudioMarkdownText(
                stringValue(content.headline) ??
                  artifact.title ??
                  t("studioTools.results.recruiterPreview"),
              )}
            </div>
            <StudioRichText
              value={
                stringValue(content.recruiterSummary) ??
                artifact.plaintext ??
                ""
              }
              style={{ ...resultBodyStyle(theme), marginTop: 4 }}
            />
          </div>
        </div>
        <BulletList
          title={t("studioTools.results.visibleStrengths")}
          items={stringList(content.visibleStrengths)}
        />
        <BulletList
          title={t("studioTools.results.concerns")}
          items={stringList(content.concerns)}
        />
        <BulletList
          title={t("studioTools.results.missingEvidence")}
          items={stringList(content.missingEvidence)}
        />
      </article>
    );
  }

  if (artifact.kind === "screening_answers") {
    const answers = objectList(content.answers);
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={t("studioTools.results.questions")}
          artifact={artifact}
        />
        <div
          style={{
            borderRadius: 22,
            border: `1px solid ${theme.borderSubtle}`,
            backgroundColor: theme.surface,
            overflow: "hidden",
          }}
        >
          {answers.slice(0, 5).map((answer, index) => (
            <div
              key={`${stringValue(answer.question) ?? index}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "12px 14px",
                borderBottom:
                  index < Math.min(answers.length, 5) - 1
                    ? `1px solid ${theme.borderSubtle}`
                    : "none",
              }}
            >
              <div
                style={{
                  width: 28,
                  color: theme.textTertiary,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Q{index + 1}
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <StudioRichText
                  value={stringValue(answer.question) ?? ""}
                  style={{
                    color: theme.text,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: "20px",
                  }}
                />
                <StudioRichText
                  value={stringValue(answer.answer) ?? ""}
                  style={resultBodyStyle(theme)}
                />
              </div>
            </div>
          ))}
        </div>
      </article>
    );
  }

  if (
    artifact.kind === "apply_checklist" ||
    artifact.kind === "application_packet"
  ) {
    const ready = content.ready === true;
    return (
      <article style={resultCardStyle(theme)}>
        <ResultHeader
          title={artifact.title ?? artifactKindLabel(artifact.kind)}
          artifact={artifact}
          subtle
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Chip
            label={
              ready
                ? t("studioTools.results.ready")
                : t("studioTools.results.needsReview")
            }
            active
          />
        </div>
        <BulletList
          title={t("studioTools.results.steps")}
          items={stringList(content.steps)}
        />
        <BulletList
          title={t("studioTools.results.blockers")}
          items={stringList(content.blockers)}
        />
      </article>
    );
  }

  return (
    <article style={resultCardStyle(theme)}>
      <ResultHeader
        title={artifact.title ?? artifactKindLabel(artifact.kind)}
        artifact={artifact}
      />
      <StudioRichText
        value={artifact.plaintext ?? t("studioTools.results.savedArtifact")}
        style={resultBodyStyle(theme)}
      />
    </article>
  );
}

export function ApplicationStudioToolScreen({ toolId }: Props) {
  const config = APPLICATION_STUDIO_TOOLS[toolId];
  const { t } = useTranslation("seeker");
  const { theme, isDark } = useTheme();
  const router = useRouter();
  const upgradeModal = useUpgradeModal();
  const params = useLocalSearchParams<{
    resumeProjectId?: string | string[];
  }>();
  const [roleTitle, setRoleTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState<StudioCountryCode>("UAE");
  const [seniority, setSeniority] = useState<StudioSeniority>("mid");
  const [jobDescription, setJobDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [generatedArtifacts, setGeneratedArtifacts] = useState<
    ApplicationStudioArtifactSummary[]
  >([]);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { data: artifacts = [] } = useApplicationStudioArtifacts({
    kind:
      config.generateKind === "application_packet"
        ? undefined
        : config.resultKinds[0],
    limit: 50,
  });

  const visibleArtifacts = useMemo(() => {
    if (generatedArtifacts.length > 0) return generatedArtifacts;
    return artifacts
      .filter((artifact) => config.resultKinds.includes(artifact.kind))
      .slice(0, config.generateKind === "application_packet" ? 8 : 1);
  }, [artifacts, config.generateKind, config.resultKinds, generatedArtifacts]);

  const currentArtifact = visibleArtifacts[0];
  usePublishPageContext({
    screen: "application-studio",
    ...(currentArtifact
      ? {
          entity: {
            type: "studioArtifact" as const,
            id: currentArtifact.id,
            ...(currentArtifact.title ? { label: currentArtifact.title } : {}),
          },
        }
      : {}),
    state: { toolId, roleTitle, companyName },
  });

  const canSubmit = roleTitle.trim().length > 0 && !generating;
  const heroSurface = isDark ? theme.surfaceElevated : "rgba(255,255,255,0.82)";
  const title = t(config.titleKey as never);
  const initials = title.trim().slice(0, 2).toUpperCase();

  useEffect(() => {
    const raw = params.resumeProjectId;
    const resumeProjectId = Array.isArray(raw) ? raw[0] : raw;
    if (!resumeProjectId) return;
    let cancelled = false;
    void fetchApplicationStudioProject(resumeProjectId)
      .then((project) => {
        if (cancelled) return;
        setRoleTitle(project.roleTitle ?? project.target.roleTitle ?? "");
        setCompanyName(project.companyName ?? project.target.companyName ?? "");
        setCountry(project.target.country ?? project.country ?? "UAE");
        const nextSeniority = project.target.seniority;
        if (nextSeniority) setSeniority(nextSeniority);
        setJobDescription(project.target.jobDescription ?? "");
        setNotes(project.target.notes ?? "");
      })
      .catch(() => {
        // The project is a resume convenience; the editable form remains usable.
      });
    return () => {
      cancelled = true;
    };
  }, [params.resumeProjectId]);

  const handleGenerate = async () => {
    if (!canSubmit) return;
    setGenerating(true);
    setErrorMessage(null);
    let draftId: string | undefined;
    try {
      const projectTitle = companyName.trim()
        ? `${roleTitle.trim()} - ${companyName.trim()}`
        : roleTitle.trim();
      const project = await createApplicationStudioProject({
        title: projectTitle,
        roleTitle: roleTitle.trim(),
        companyName: config.companyVisible ? companyName.trim() || null : null,
        country: config.countryVisible ? country : null,
        seniority: config.seniorityVisible ? seniority : null,
        jobDescription: config.jobDescriptionVisible
          ? jobDescription.trim() || null
          : null,
        notes: config.notesVisible ? notes.trim() || null : null,
      });
      draftId = project.id;
      const prompt = [
        notes.trim(),
        jobDescription.trim()
          ? `Job description:\n${jobDescription.trim()}`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n");
      const result = await generateApplicationStudioArtifact(project.id, {
        kind: config.generateKind,
        prompt: prompt || null,
      });
      setGeneratedArtifacts(result.artifacts);
      trackProductEvent(STUDIO_TOOL_ANALYTICS_EVENTS[toolId], {
        tool: toolId,
        kind: config.generateKind,
        hasJobDescription: Boolean(jobDescription.trim()),
        hasCompany: Boolean(companyName.trim()),
      });
    } catch (error) {
      if (isApplicationStudioEntitlementDenied(error)) {
        trackProductEvent("feature_blocked", {
          tool: toolId,
          placement: "studio_generate",
        });
        const returnTo = draftId
          ? `${config.route}?resumeProjectId=${encodeURIComponent(draftId)}`
          : config.route;
        upgradeModal.open(
          error.decision
            ? createUpgradeContext({
                decision: error.decision,
                placement: "studio_generate",
                entryPoint: `application_studio.${toolId}`,
                returnTo,
                draftId,
              })
            : {
                placement: "studio_generate",
                entryPoint: `application_studio.${toolId}`,
                planSet: "individual",
                recommendedTier: config.recommendedTier,
                featureKey: config.featureKey,
                returnTo,
                draftId,
              },
        );
        return;
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("studioTools.errors.generateBody"),
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="h-full overflow-y-auto"
      style={{ backgroundColor: "var(--bg)" }}
    >
      <div className="tabbio-svc-page">
        <WebPageHeader
          title={title}
          subtitle={t(config.subtitleKey as never)}
          onBack={() => dismissToServices(router)}
        />

        <section
          style={{
            borderRadius: 30,
            border: `1px solid ${theme.borderSubtle}`,
            backgroundColor: heroSurface,
            padding: 18,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 999,
                background: isDark
                  ? `linear-gradient(180deg, ${theme.surfaceSecondary}, ${theme.surfaceTertiary})`
                  : `linear-gradient(180deg, ${colors.gray[100]}, ${colors.gray[200]})`,
                border: `1px solid ${theme.borderSubtle}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isDark ? theme.textSecondary : colors.gray[600],
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {initials}
            </div>
            <div
              style={{
                minHeight: 64,
                display: "flex",
                alignItems: "center",
                color: theme.textTertiary,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {t("studioTools.form.context")}
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {config.outcomeKeys.map((key) => (
              <Chip key={key} label={t(key as never)} />
            ))}
          </div>
        </section>

        <section style={sectionWrapStyle}>
          <div style={sectionLabelStyle(theme)}>
            {t("studioTools.form.inputs")}
          </div>
          <div style={cardStyle(theme, heroSurface)}>
            <Field
              label={t("studioTools.form.role")}
              value={roleTitle}
              onChange={setRoleTitle}
              placeholder={t(config.rolePlaceholderKey as never)}
            />

            {config.companyVisible ? (
              <Field
                label={t("studioTools.form.company")}
                value={companyName}
                onChange={setCompanyName}
                placeholder={t("studioTools.form.companyPlaceholder")}
              />
            ) : null}

            {config.countryVisible ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{t("studioTools.form.country")}</Label>
                <StudioCountryPicker
                  value={country}
                  onChange={setCountry}
                  title={t("studioTools.form.country")}
                  searchPlaceholder={t(
                    "studioTools.form.countrySearchPlaceholder",
                  )}
                />
              </div>
            ) : null}

            {config.seniorityVisible ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Label>{t("studioTools.form.seniority")}</Label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {STUDIO_SENIORITIES.map((item) => (
                    <Chip
                      key={item.value}
                      label={t(item.key as never)}
                      active={seniority === item.value}
                      onClick={() => setSeniority(item.value)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {config.jobDescriptionVisible ? (
              <Field
                label={t("studioTools.form.jobDescription")}
                value={jobDescription}
                onChange={setJobDescription}
                placeholder={t("studioTools.form.jobDescriptionPlaceholder")}
                multiline
              />
            ) : null}

            {config.notesVisible ? (
              <Field
                label={t("studioTools.form.notes")}
                value={notes}
                onChange={setNotes}
                placeholder={t("studioTools.form.notesPlaceholder")}
                multiline
                compact
              />
            ) : null}

            {errorMessage ? (
              <div role="alert" style={errorStyle}>
                {errorMessage}
              </div>
            ) : null}

            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleGenerate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 48,
                padding: "13px 18px",
                borderRadius: 999,
                border: "none",
                background: canSubmit ? colors.primary : theme.surfaceSecondary,
                color: canSubmit ? "#fff" : theme.textTertiary,
                fontSize: 15,
                fontWeight: 600,
                cursor: canSubmit ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >
              {generating
                ? t("common:labels.loading")
                : t(config.generateKey as never)}
            </button>
          </div>
        </section>

        <section style={sectionWrapStyle}>
          <div style={sectionLabelStyle(theme)}>
            {visibleArtifacts.length > 0
              ? t("studioTools.results.latest")
              : t("studioTools.results.empty")}
          </div>
          {visibleArtifacts.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {visibleArtifacts.map((artifact) => (
                <ResultRenderer
                  key={artifact.id}
                  artifact={artifact}
                  compact={config.generateKind === "application_packet"}
                />
              ))}
            </div>
          ) : (
            <div style={cardStyle(theme, heroSurface)}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: theme.text,
                  textAlign: "center",
                }}
              >
                {t("studioTools.results.emptyTitle")}
              </div>
              <div
                style={{
                  fontSize: 14,
                  lineHeight: "21px",
                  color: theme.textSecondary,
                  textAlign: "center",
                }}
              >
                {t("studioTools.results.emptyBody")}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const sectionWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 18,
};

function sectionLabelStyle(
  theme: ReturnType<typeof useTheme>["theme"],
): React.CSSProperties {
  return {
    fontSize: 16,
    fontWeight: 600,
    color: theme.text,
  };
}

function cardStyle(
  theme: ReturnType<typeof useTheme>["theme"],
  backgroundColor: string,
): React.CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    borderRadius: 28,
    border: `1px solid ${theme.borderSubtle}`,
    backgroundColor,
    padding: 22,
  };
}

function resultCardStyle(
  theme: ReturnType<typeof useTheme>["theme"],
): React.CSSProperties {
  return {
    ...cardStyle(theme, theme.surface),
    gap: 16,
  };
}

function resultKickerStyle(
  theme: ReturnType<typeof useTheme>["theme"],
): React.CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 600,
    color: theme.textTertiary,
  };
}

function resultTitleStyle(
  theme: ReturnType<typeof useTheme>["theme"],
): React.CSSProperties {
  return {
    fontSize: 18,
    lineHeight: "24px",
    fontWeight: 700,
    color: theme.text,
  };
}

function resultBodyStyle(
  theme: ReturnType<typeof useTheme>["theme"],
): React.CSSProperties {
  return {
    fontSize: 14,
    lineHeight: "20px",
    color: theme.textSecondary,
    whiteSpace: "pre-wrap",
  };
}

const errorStyle: React.CSSProperties = {
  borderRadius: 14,
  border: "1px solid rgba(217, 45, 32, 0.24)",
  background: "rgba(217, 45, 32, 0.08)",
  color: "var(--color-danger, #d92d20)",
  padding: "12px 14px",
  fontSize: 13,
  lineHeight: "18px",
  fontWeight: 600,
};
